/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import {formatDate} from '@angular/common';
import {Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {FormControl} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {Observable} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {ALL, DISMISS_MSG} from '../../../../common/constants';
import {StateService} from '../../../../core/state/state.service';
import {ArchDevianceService} from '../../../../services/arch-deviance.service';
import {ExcelExportService} from '../../../../services/excel-export.service';
import {VehicleAttributesService} from '../../../../services/vehicle-attributes.service';
import {UpdateAttributePopupComponent} from '../update-attribute-popup/update-attribute-popup.component';

interface Menu {
  filteredOptions: Observable<string[]>;
  availableOptions: any[];
  selectedOptions: string;
  searchAttribute: string;
  model: FormControl;
  key: string;
  maxLength: number;
}

@Component({
    selector: 'app-vehicle-attributes',
    templateUrl: './vehicle-attributes.component.html',
    styleUrls: ['./vehicle-attributes.component.scss'],
    standalone: false
})

export class VehicleAttributesComponent implements OnInit {
  showSpinner: boolean;
  existNonCompliantProgramCode = false;
  ALL = ALL;

  regions: Menu = {
    filteredOptions: null,
    availableOptions: [],
    selectedOptions: '%',
    searchAttribute: 'region',
    model: new FormControl(),
    key: 'region',
    maxLength: 0
  };

  subRegions: Menu = {
    filteredOptions: null,
    availableOptions: [],
    selectedOptions: '%',
    searchAttribute: 'subregion',
    model: new FormControl(),
    key: 'subregion',
    maxLength: 0
  };

  planningMarkets: Menu = {
    filteredOptions: null,
    availableOptions: [],
    selectedOptions: '%',
    searchAttribute: 'planningMarket',
    model: new FormControl(),
    key: 'PLNGMKT',
    maxLength: 0
  };

  architectures: Menu = {
    filteredOptions: null,
    availableOptions: [],
    selectedOptions: '%',
    searchAttribute: 'architecture',
    model: new FormControl(),
    key: 'ARCH',
    maxLength: 25
  };

  marketDivisions: Menu = {
    filteredOptions: null,
    availableOptions: [],
    selectedOptions: '%',
    searchAttribute: 'marketDivision',
    model: new FormControl(),
    key: 'MKTDIV',
    maxLength: 0
  };

  brands: Menu = {
    filteredOptions: null,
    availableOptions: [],
    selectedOptions: '%',
    searchAttribute: 'brand',
    model: new FormControl(),
    key: 'BRAND',
    maxLength: 50
  };

  localBrands: Menu = {
    filteredOptions: null,
    availableOptions: [],
    selectedOptions: '%',
    searchAttribute: 'localBrand',
    model: new FormControl(),
    key: 'LBRAND',
    maxLength: 50
  };

  programCodes: Menu = {
    filteredOptions: null,
    availableOptions: [],
    selectedOptions: '%',
    searchAttribute: 'programCode',
    model: new FormControl(),
    key: 'PRGMCODE',
    maxLength: 15
  };

  styleCodes: Menu = {
    filteredOptions: null,
    availableOptions: [],
    selectedOptions: '%',
    searchAttribute: 'styleCode',
    model: new FormControl(),
    key: 'STYLECODE',
    maxLength: 15
  };

  bodyStyles: Menu = {
    filteredOptions: null,
    availableOptions: [],
    selectedOptions: '%',
    searchAttribute: 'bodyStyle',
    model: new FormControl(),
    key: 'BODYSTYLE',
    maxLength: 25
  };

  vehicleTypes: Menu = {
    filteredOptions: null,
    availableOptions: [],
    selectedOptions: '%',
    searchAttribute: 'vehicleType',
    model: new FormControl(),
    key: 'VEHTYPE',
    maxLength: 10
  };

  menuArray: Menu[] = [this.vehicleTypes, this.bodyStyles, this.styleCodes, this.programCodes, this.localBrands,
    this.brands, this.marketDivisions, this.architectures, this.planningMarkets,
    this.subRegions, this.regions];

  @ViewChild('dialogBox') dialogBox: TemplateRef<any>;

  constructor(private readonly vehicleAttributesService: VehicleAttributesService,
              public archDevianceService: ArchDevianceService,
              public dialog: MatDialog,
              private excelExport: ExcelExportService,
              private user: StateService,
              public snackBar: FabMessageService) {
  }

  ngOnInit(): void {
    this.checkForNonComplaintProgramCode();
  }

  /**
   * Check if any non compliant program code exist, if nothing is returned then load the filter options.
   * If data is returned then display alert message, dont not load filter options and disable the reset button.
   */
  checkForNonComplaintProgramCode(): void {
    this.showSpinner = true;
    this.archDevianceService.getNonCompliantProgramCodes().subscribe( data => {
      if (data.length > 0) {
        this.showSpinner = false;
        this.existNonCompliantProgramCode = true;
        alert('Run Manage Architecture Deviances tool before changing Vehicle Attributes');
      } else {
        this.getFilterOptions();
      }
    }, (error) => {
      this.showSpinner = false;
      this.snackBar.displayError(error, DISMISS_MSG);
    });
  }

  /**
   * Get filter option.
   * @param attribute is a column name used to retrieve data from DB.
   * @param menu will be assigned value retrieved from DB.
   */
  getData(attribute: string, menu: Menu): void {
    this.vehicleAttributesService.getFilterOptions(attribute, this.regions.selectedOptions,
      this.subRegions.selectedOptions, this.planningMarkets.selectedOptions,
      this.architectures.selectedOptions, this.marketDivisions.selectedOptions,
      this.brands.selectedOptions, this.localBrands.selectedOptions,
      this.programCodes.selectedOptions, this.styleCodes.selectedOptions,
      this.bodyStyles.selectedOptions, this.vehicleTypes.selectedOptions).subscribe(data => {
        menu.availableOptions = data.map(a => a[menu.key]);
        menu.availableOptions.unshift(ALL);
        menu.model.setValue(ALL);
        this.updateFilteredDropdownOptions(menu);
        this.updateSelectedValue(menu);
      },
      (error) => {
        this.showSpinner = false;
        this.snackBar.displayError(error, DISMISS_MSG);
      }, () => {
        if (menu.searchAttribute === this.vehicleTypes.searchAttribute){
          this.showSpinner = false;
        }
      });
  }

  /**
   * Generate report of all data that will be updated.
   * @param attribute is a column name used to retrieve data from DB.
   * @param currentValue is an existing value in DB that need to be updated.
   * @param newValue will replace 'currentValue'.
   */
  previewChanges(attribute: string, currentValue: string, newValue: string): void {
    this.vehicleAttributesService.getPreviewChanges(attribute, currentValue, newValue,
      this.regions.selectedOptions, this.subRegions.selectedOptions, this.planningMarkets.selectedOptions,
      this.architectures.selectedOptions, this.marketDivisions.selectedOptions, this.brands.selectedOptions,
      this.localBrands.selectedOptions, this.programCodes.selectedOptions, this.styleCodes.selectedOptions,
      this.bodyStyles.selectedOptions, this.vehicleTypes.selectedOptions).subscribe(
      response => {
        this.showSpinner = false;
        const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
        this.excelExport.exportToExcelFromServer(response, 'ChangeAttributeVeh_' + currDate);
      },
      (error) => {
        this.showSpinner = false;
        this.snackBar.displayError(error, DISMISS_MSG);
      });
  }

  /**
   * Update an attribute value, and generate before and after changes report.
   * @param attribute is a column name used to retrieve data from DB.
   * @param currentValue is an existing value in DB that need to be updated.
   * @param newValue will replace 'currentValue'.
   */
  commitChanges(attribute: string, currentValue: string, newValue: string): void {
    this.vehicleAttributesService.getCommitChanges(attribute, currentValue, newValue,
      this.regions.selectedOptions, this.subRegions.selectedOptions, this.planningMarkets.selectedOptions,
      this.architectures.selectedOptions, this.marketDivisions.selectedOptions, this.brands.selectedOptions,
      this.localBrands.selectedOptions, this.programCodes.selectedOptions, this.styleCodes.selectedOptions,
      this.bodyStyles.selectedOptions, this.vehicleTypes.selectedOptions, this.user.getUserGmid()).subscribe(
      response => {
        const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
        this.excelExport.exportToExcelFromServer(response, 'ChangeAttributeVeh_' + currDate);
      },
      (error) => {
        this.showSpinner = false;
        this.snackBar.displayError(error, DISMISS_MSG);
      }, () => {
        this.checkForNonComplaintProgramCode();
        this.showSpinner = false;
      });
  }

  /**
   * Open a popup to preview/commit changes.
   * @param title of the Filter option that will be updated.
   * @param currentValue is an existing value in DB that need to be updated.
   * @param attribute is a column name used to retrieve data from DB.
   * @param length is number of char allowed for specific attribute.
   */
  openDialog(title: string, currentValue: string, attribute: string, length: number): void {
    const dialogRef = this.dialog.open(UpdateAttributePopupComponent, {
      data: {
        header: title,
        selectedValue: currentValue,
        attributeToUpdate: attribute,
        maxLength: length
      }
    });

    dialogRef.afterClosed().subscribe(res => {
      // received data from dialog-component.
      if (res) {
        this.showSpinner = true;
        if (res.data.isPreviewChange) {
          this.previewChanges(attribute, currentValue, res.data.newValue);
        } else {
          this.commitChanges(attribute, currentValue, res.data.newValue);
        }
      }
    });
  }

  /**
   * Get default filter options.
   */
  getFilterOptions(): void {
    for (const menu of this.menuArray) {
      this.getData(menu.searchAttribute, menu);
      menu.model.setValue(ALL, {emitEvent: false});
    }
  }

  /**
   * Replace 'All' with '%'.
   * @param menu Current selected filter option.
   */
  replaceAllValue(menu: Menu): void {
    if (menu.selectedOptions === ALL) {
      menu.selectedOptions = '%';
    }
  }

  /**
   * If available options only have 1 value except 'All', select the only value.
   * @param menu selected menu option
   */
  updateSelectedValue(menu: Menu): void {
    if (menu.availableOptions.length === 2) {
      menu.model.setValue(menu.availableOptions[1]);
      menu.selectedOptions = menu.availableOptions[1];
    }
  }

  /**
   * On Value Change filter data.
   * @param menu current filter option.
   */
  updateFilteredDropdownOptions(menu: Menu): void {
    menu.filteredOptions = menu.model.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(menu.availableOptions, value || ''))
    );
  }

  /**
   * Filters option by search string; show all options when searching "All".
   * @param options, all available options.
   * @param value users input.
   */
  _filter(options: string[], value: string): string[] {
    const filterValue = value.toLowerCase();
    return options.filter(option => filterValue === ALL.toLowerCase()
      ? option.toLowerCase().includes('') : option.toLowerCase().includes(filterValue));
  }

  /**
   * If dropdown value is All, then set text field value to empty on click.
   * @param menu selected dropdown
   */
  resetText(menu: Menu): void {
    if (menu.model.value === ALL) {
      menu.model.setValue('');
    }
  }

  /**
   * Reset all filter options.
   */
  resetFilter(): void {
    this.showSpinner = true;
    this.resetSelectionAndBelow('');
    this.getData(this.regions.searchAttribute, this.regions);
    this.onChangeRegion();
  }

  /**
   * Reset selection of all the remaining filters
   * @param searchAttribute used to identify with filter was selected.
   */
  resetSelectionAndBelow(searchAttribute): void {
    for (const menu of this.menuArray) {
      if (searchAttribute.toLowerCase() === menu.searchAttribute.toLowerCase()) {
        break;
      }
      if (searchAttribute.toLowerCase() !== menu.searchAttribute) {
        menu.model.setValue(ALL);
        menu.selectedOptions = '%';
        menu.filteredOptions = null;
      }
    }
  }

  /**
   * Called when a region is selected.
   * If 'All' is selected for regions, set selected region values to '%'.
   * Call getData to retrieve values for subRegions.
   * Call onChangeSubRegions to retrieve filter options for other dropdowns.
   */
  onChangeRegion(): void {
    this.showSpinner = true;
    this.resetSelectionAndBelow(this.regions.searchAttribute);
    this.regions.selectedOptions = this.regions.model.value;
    this.replaceAllValue(this.regions);
    this.getData(this.subRegions.searchAttribute, this.subRegions);
    this.onChangeSubRegion();
  }

  /**
   * Called when a subRegion is selected.
   * If 'All' is selected for subRegions, set selected subRegions values to '%'.
   * Call getData to retrieve values for planningMarket.
   * Call onChangePlanningMarket to retrieve filter options for other dropdowns.
   */
  onChangeSubRegion(): void {
    this.showSpinner = true;
    this.resetSelectionAndBelow(this.subRegions.searchAttribute);
    this.subRegions.selectedOptions = this.subRegions.model.value;
    this.replaceAllValue(this.subRegions);
    this.getData(this.planningMarkets.searchAttribute, this.planningMarkets);
    this.onChangePlanningMarket();
  }

  /**
   * Called when a planningMarket is selected.
   * If 'All' is selected for planningMarkets, set selected planningMarkets values to '%'.
   * Call getData to retrieve values for architectures.
   * Call onChangeArchitecture to retrieve filter options for other dropdowns.
   */
  onChangePlanningMarket(): void {
    this.showSpinner = true;
    this.resetSelectionAndBelow(this.planningMarkets.searchAttribute);
    this.planningMarkets.selectedOptions = this.planningMarkets.model.value;
    this.replaceAllValue(this.planningMarkets);
    this.getData(this.architectures.searchAttribute, this.architectures);
    this.onChangeArchitecture();
  }

  /**
   * Called when a architecture is selected.
   * If 'All' is selected for architectures, set selected architectures values to '%'.
   * Call getData to retrieve values for marketDivisions.
   * Call onChangeMarketDivision to retrieve filter options for other dropdowns.
   */
  onChangeArchitecture(): void {
    this.showSpinner = true;
    this.resetSelectionAndBelow(this.architectures.searchAttribute);
    this.architectures.selectedOptions = this.architectures.model.value;
    this.replaceAllValue(this.architectures);
    this.getData(this.marketDivisions.searchAttribute, this.marketDivisions);
    this.onChangeMarketDivision();
  }

  /**
   * Called when a marketDivision is selected.
   * If 'All' is selected for marketDivisions, set selected marketDivisions values to '%'.
   * Call getData to retrieve values for brands.
   * Call onChangeBrand to retrieve filter options for other dropdowns.
   */
  onChangeMarketDivision(): void {
    this.showSpinner = true;
    this.resetSelectionAndBelow(this.marketDivisions.searchAttribute);
    this.marketDivisions.selectedOptions = this.marketDivisions.model.value;
    this.replaceAllValue(this.marketDivisions);
    this.getData(this.brands.searchAttribute, this.brands);
    this.onChangeBrand();
  }

  /**
   * Called when a brand is selected.
   * If 'All' is selected for brands, set selected brands values to '%'.
   * Call getData to retrieve values for localBrands.
   * Call onChangeLocalBrand to retrieve filter options for other dropdowns.
   */
  onChangeBrand(): void {
    this.showSpinner = true;
    this.resetSelectionAndBelow(this.brands.searchAttribute);
    this.brands.selectedOptions = this.brands.model.value;
    this.replaceAllValue(this.brands);
    this.getData(this.localBrands.searchAttribute, this.localBrands);
    this.onChangeLocalBrand();
  }

  /**
   * Called when a localBrand is selected.
   * If 'All' is selected for localBrands, set selected localBrands values to '%'.
   * Call getData to retrieve values for programCodes.
   * Call onChangeProgramCode to retrieve filter options for other dropdowns.
   */
  onChangeLocalBrand(): void {
    this.showSpinner = true;
    this.resetSelectionAndBelow(this.localBrands.searchAttribute);
    this.localBrands.selectedOptions = this.localBrands.model.value;
    this.replaceAllValue(this.localBrands);
    this.getData(this.programCodes.searchAttribute, this.programCodes);
    this.onChangeProgramCode();
  }

  /**
   * Called when a programCode is selected.
   * If 'All' is selected for programCodes, set selected programCodes values to '%'.
   * Call getData to retrieve values for styleCodes.
   * Call onChangeStyleCode to retrieve filter options for other dropdowns.
   */
  onChangeProgramCode(): void {
    this.showSpinner = true;
    this.resetSelectionAndBelow(this.programCodes.searchAttribute);
    this.programCodes.selectedOptions = this.programCodes.model.value;
    this.replaceAllValue(this.programCodes);
    this.getData(this.styleCodes.searchAttribute, this.styleCodes);
    this.onChangeStyleCode();
  }

  /**
   * Called when a styleCode is selected.
   * If 'All' is selected for styleCodes, set selected styleCodes values to '%'.
   * Call getData to retrieve values for bodyStyles.
   * Call onChangeBodyStyle to retrieve filter options for other dropdowns.
   */
  onChangeStyleCode(): void {
    this.showSpinner = true;
    this.resetSelectionAndBelow(this.styleCodes.searchAttribute);
    this.styleCodes.selectedOptions = this.styleCodes.model.value;
    this.replaceAllValue(this.styleCodes);
    this.getData(this.bodyStyles.searchAttribute, this.bodyStyles);
    this.onChangeBodyStyle();
  }

  /**
   * Called when a bodyStyle is selected.
   * If 'All' is selected for bodyStyles, set selected bodyStyles values to '%'.
   * Call getData to retrieve values for vehicleTypes.
   * Call onChangeVehicleType to retrieve filter options for other dropdowns.
   */
  onChangeBodyStyle(): void {
    this.showSpinner = true;
    this.resetSelectionAndBelow(this.bodyStyles.searchAttribute);
    this.bodyStyles.selectedOptions = this.bodyStyles.model.value;
    this.replaceAllValue(this.bodyStyles);
    this.getData(this.vehicleTypes.searchAttribute, this.vehicleTypes);
    this.onChangeVehicleType();
  }

  /**
   * On selection set vehicleType.
   * If 'All' is selected for bodyStyles, set selected bodyStyles values to '%'.
   */
  onChangeVehicleType(): void {
    this.vehicleTypes.selectedOptions = this.vehicleTypes.model.value;
    this.replaceAllValue(this.vehicleTypes);
  }

}
